import { ReasoningStep, SuggestedQuestion } from './actions';
import { CHAT_CONFIG, clearMemoryService, getChatHistoryService, sendChatRequest } from './service';

interface Message {
    role: string;
    content: string;
}

interface StreamChunk {
    delta: {
        role: string;
        content: string;
    };
    message: {
        role: string;
        content: string;
    };
    is_final: boolean;
    suggested_questions?: SuggestedQuestion[];
    has_suggested_questions?: boolean;
    reasoning?: ReasoningStep[] | null;
    has_new_reasoning?: boolean;
    metadata: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
    };
}

export interface AskAicaResponse {
    content: string;
    sources?: string[];
    suggestedQuestions?: SuggestedQuestion[];
    hasNewSuggestedQuestions?: boolean;
    reasoning?: ReasoningStep[] | null;
    hasNewReasoning?: boolean;
    metadata?: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
    };
}

/**
 * Helper function to send messages to the Ask Aica API with streaming enabled
 *
 * This implementation uses XMLHttpRequest for better control over the streaming request
 * to avoid any browser quirks with fetch API and SSE implementation
 *
 * @param messages Array of messages in the conversation
 * @param onChunk Callback that receives each chunk of the stream
 * @param onComplete Callback that is called when the stream is complete
 * @param onError Callback for error handling
 * @returns A function to abort the stream
 */
export const streamAskAicaChat = (
    messages: Message[],
    sources: string[],
    user_id: string,
    org_id: string,
    onChunk: (
        chunk: string,
        suggestedQuestions?: SuggestedQuestion[],
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        isFirst?: boolean,
        metadata?: any,
    ) => void,
    onComplete: (response: AskAicaResponse) => void,
    onError: (error: any) => void,
): (() => void) => {
    // Create a controlled XHR for streaming
    const xhr = new XMLHttpRequest();

    // Variables to track response data
    let fullContent = '';
    let isFirstChunk = true;
    let responseSuggestedQuestions: SuggestedQuestion[] | undefined = undefined;
    let responseReasoning: ReasoningStep[] | null = null;
    let hasNewReasoning: boolean = false;
    let responseMetadata: StreamChunk['metadata'] | undefined = undefined;
    let buffer = '';

    // Set up the request
    xhr.open('POST', CHAT_CONFIG.URL);

    // Set headers
    const headers = CHAT_CONFIG.getHeaders(org_id, user_id);
    Object.keys(headers).forEach((key) => {
        xhr.setRequestHeader(key, headers[key]);
    });

    // Set up streaming event handling
    xhr.onreadystatechange = function () {
        // Process partial data (readyState 3) and complete data (readyState 4)
        if (xhr.readyState === 3 || xhr.readyState === 4) {
            // Get the new data since last progress
            const newData = xhr.responseText.substring(buffer.length);
            buffer = xhr.responseText;

            if (newData) {
                // Split the new data by lines
                const lines = newData.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        // Skip [DONE] message
                        if (line.includes('[DONE]')) continue;

                        try {
                            // Extract the JSON data from the SSE format
                            const jsonData = line.slice(5).trim();
                            const parsedData: StreamChunk = JSON.parse(jsonData);

                            // Add to the full content from delta
                            if (parsedData.delta && parsedData.delta.content) {
                                fullContent += parsedData.delta.content;
                            }

                            // Track suggested questions if present
                            if (parsedData.suggested_questions && parsedData.has_suggested_questions) {
                                responseSuggestedQuestions = parsedData.suggested_questions;
                            }

                            // Track reasoning if present
                            responseReasoning = parsedData.reasoning ?? null;
                            hasNewReasoning = parsedData.has_new_reasoning ?? false;

                            // Save metadata
                            if (responseMetadata === undefined) {
                                responseMetadata = parsedData.metadata;
                            }

                            // Check if this is an error response
                            let isError = false;
                            let errorMessage = '';
                            if (Array.isArray(parsedData.reasoning)) {
                                const errorReason = parsedData.reasoning.find((step) => step.type === 'error');
                                if (errorReason) {
                                    isError = true;
                                    errorMessage = errorReason.message;
                                }
                            }

                            // Create metadata with error information
                            const chunkMetadata = {
                                ...parsedData.metadata,
                                ...(isError && { error: true, errorMessage }),
                            };

                            // Call the onChunk callback with all relevant data
                            if (isFirstChunk) {
                                const currentIsFirst = isFirstChunk;
                                isFirstChunk = false;
                                onChunk(
                                    parsedData.delta.content || '',
                                    parsedData.suggested_questions,
                                    parsedData.reasoning,
                                    hasNewReasoning,
                                    currentIsFirst,
                                    chunkMetadata,
                                );
                            } else {
                                onChunk(
                                    parsedData.delta.content || '',
                                    parsedData.suggested_questions,
                                    parsedData.reasoning,
                                    hasNewReasoning,
                                    false,
                                    chunkMetadata,
                                );
                            }
                        } catch (error) {
                            console.error('Error parsing SSE data:', error, line);
                        }
                    }
                }
            }
        }

        // Handle completion
        if (xhr.readyState === 4) {
            if (xhr.status >= 200 && xhr.status < 300) {
                onComplete({
                    content: fullContent,
                    suggestedQuestions: responseSuggestedQuestions,
                    hasNewSuggestedQuestions: !!responseSuggestedQuestions,
                    reasoning: responseReasoning,
                    hasNewReasoning,
                    metadata: responseMetadata,
                });
            } else {
                onError(new Error(`HTTP error! Status: ${xhr.status}`));
            }
        }
    };

    // Handle errors
    xhr.onerror = function () {
        onError(new Error('Network error occurred'));
    };

    // Send the request
    xhr.send(
        JSON.stringify({
            user_id,
            org_id,
            sources,
            messages,
            stream: true,
        }),
    );

    // Return a function to abort the request
    return () => {
        xhr.abort();
    };
};

/**
 * Helper function to send messages to the Ask Aica API without streaming
 * @param messages Array of messages in the conversation
 * @returns Promise with the response
 */
export const askAicaChat = async (messages: Message[]): Promise<AskAicaResponse> => {
    try {
        const response = await sendChatRequest({ messages });
        return {
            content: response.data.content,
            metadata: response.data.metadata,
        };
    } catch (error) {
        console.error('Error in askAicaChat:', error);
        throw error;
    }
};

export const getChatHistory = async (userId, orgId, pageNum, pageSize) => {
    try {
        const response = await getChatHistoryService({ userId, orgId, pageNum, pageSize });
        return response.data;
    } catch (err) {
        console.error('Error in getChatHistory:', err);
        throw err;
    }
};

export const clearMemory = async (userId: string, orgId: string) => {
    try {
        const response = await clearMemoryService({ userId, orgId });
        return response.data;
    } catch (err) {
        console.error('Error in clearMemory:', err);
        throw err;
    }
};
